import React, { useState } from "react";
import { MentorAvatar } from "./Logo";
import { Sparkles, ArrowRight, Check, Volume2, ShieldAlert, Award, Star, Mic } from "lucide-react";

export default function InterviewFearPage({ onOpenModal }) {
  const [mockLevel, setMockLevel] = useState("FAANG");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [coachingResult, setCoachingResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Audio Snippet Audio mock states
  const [playingAudio, setPlayingAudio] = useState(null);

  const mockQuestions = {
    FAANG: [
      "Tell me about yourself. (How do you introduce your tier-2/3 background with high impact?)",
      "Describe a challenging college project. How do you explain technical details clearly without feeling inferior?"
    ],
    Tier1: [
      "Explain the difference between a list and an array in memory. Walk me through it in simple, clear English.",
      "How do you handle a production bug under pressure? Speak with confidence using structural guidelines."
    ],
    Startup: [
      "Why should we hire you over an elite college graduate? Pitch your real-world proof of work.",
      "How do you handle ambiguous specifications in a product sprint? Walk me through your design priorities."
    ]
  };

  const submitAnswer = (e) => {
    e.preventDefault();
    if (!userAnswer) return;
    setAnalyzing(true);
    setCoachingResult(null);
    setTimeout(() => {
      setAnalyzing(false);
      setCoachingResult({
        vocalConfidence: 45,
        technicalAccuracy: 52,
        structureRating: 38,
        coachingFeedback: "⚠️ ANXIETY GAP: Your technical core is solid, but hesitation is slowing you down. Standard Tier-1 graduates have dozens of mock trials. With Mr. Vedula's Interview Anchor, we build high-impact communication loops so you speak with complete executive authority."
      });
    }, 1800);
  };

  const playFeedbackAudio = (id) => {
    if (playingAudio === id) {
      setPlayingAudio(null);
    } else {
      setPlayingAudio(id);
      setTimeout(() => setPlayingAudio(null), 4000); // Mock play timeout
    }
  };

  return (
    <div className="bg-[#F8F9FA] text-[#1A1A1A] font-serif pt-20">
      
      {/* 1. Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-rose-50/60 to-[#F8F9FA] py-16 md:py-24 border-b border-[#E9ECEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest bg-rose-500/10 text-[#8B0000] border border-rose-500/20">
              <Sparkles className="h-3 w-3" /> Vernacular to Corporate Bridge
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-serif tracking-tight text-[#8B0000] leading-[1.15]" data-testid="hero-heading">
              English hesitations, <span className="text-[#D90429] italic font-serif">fear of elite grads?</span>
            </h1>
            
            <h2 className="text-lg sm:text-xl font-semibold font-serif text-[#1A1A1A] tracking-tight leading-relaxed">
              Unlock Your Career Clarity: From stuck and self-conscious to communicating like a top professional.
            </h2>
            
            <p className="text-slate-600 font-sans text-sm sm:text-base max-w-xl leading-relaxed">
              Do your hands shake or your voice crack when competing with fluent, high-society candidates? Language is just a tool, not a measure of intelligence. We teach you how to project unshakable authority.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2 font-sans">
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Interview Fear Strategy")}
                className="px-6 py-4 bg-[#8B0000] hover:bg-[#6B0000] text-white font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-rose-950/20 transition-all"
                data-testid="book-free-clarity-call-btn"
              >
                Book Free Clarity Call <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Interview Fear Strategy")}
                className="px-6 py-4 bg-slate-200 hover:bg-slate-300 text-slate-800 font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 transition-all border border-slate-300"
                data-testid="register-for-free-webinar-btn"
              >
                Register for Free Webinar
              </button>
            </div>
          </div>

          {/* Right Column: Interactive Interview simulator widget */}
          <div className="lg:col-span-5 bg-white border border-slate-200 rounded-3xl p-6 shadow-xl space-y-4 font-sans">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <span className="text-xs font-black uppercase tracking-wider text-[#8B0000] flex items-center gap-1">
                <Mic className="h-3.5 w-3.5 text-[#D90429] animate-pulse" /> Tier-2/3 Stress-Test Simulator
              </span>
              <div className="flex gap-1.5 text-[10px] font-bold">
                {["Introduce", "Technical", "Sling-Pitch"].map((t, i) => {
                  const keys = ["FAANG", "Tier1", "Startup"];
                  return (
                    <button
                      key={t}
                      onClick={() => { setMockLevel(keys[i]); setCurrentQuestion(0); setCoachingResult(null); setUserAnswer(""); }}
                      className={`px-2 py-0.5 rounded ${mockLevel === keys[i] ? "bg-[#8B0000] text-white" : "bg-slate-100 text-slate-600"}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase text-[#D90429] tracking-wider block">Coaching Challenge Question:</span>
              <p className="text-sm font-semibold text-slate-900 leading-relaxed bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                "{mockQuestions[mockLevel][currentQuestion]}"
              </p>
            </div>

            <form onSubmit={submitAnswer} className="space-y-3">
              <textarea
                required
                rows="2"
                placeholder="Type or simulate speaking your technical structure..."
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8B0000] resize-none"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                data-testid="mock-interview-textarea"
              />
              <button
                type="submit"
                disabled={analyzing}
                className="w-full py-2.5 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all"
                data-testid="mock-interview-submit-btn"
              >
                {analyzing ? "Auditing Structural Vitals..." : "Audit My Answer"}
              </button>
            </form>

            {coachingResult && (
              <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl animate-fade-in space-y-2 text-xs">
                <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-bold">
                  <div className="bg-white p-2 rounded-lg border border-rose-100">
                    <span className="text-slate-500 block">Confidence</span>
                    <span className="text-red-600 text-sm font-black">{coachingResult.vocalConfidence}%</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-rose-100">
                    <span className="text-slate-500 block">Technical Accuracy</span>
                    <span className="text-[#8B0000] text-sm font-black">{coachingResult.technicalAccuracy}%</span>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-rose-100">
                    <span className="text-slate-500 block">STAR Structure</span>
                    <span className="text-red-500 text-sm font-black">{coachingResult.structureRating}%</span>
                  </div>
                </div>
                <p className="text-red-900 leading-relaxed font-medium pt-1">
                  {coachingResult.coachingFeedback}
                </p>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* 2. Pain Identification */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs font-black uppercase tracking-wider text-[#8B0000] font-sans">Confidence Diagnostic</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold font-serif text-[#1A1A1A] mt-2 mb-4" data-testid="pain-heading">
            Is this your struggle?
          </h2>
          <div className="space-y-4 text-lg text-slate-700 font-sans font-medium">
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600" /> English communication gaps make you freeze under pressure.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600" /> Feeling inferior when interviewing alongside fluent, metro-city candidates.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600" /> Lack of high-quality mock trial practice and preparation.
            </p>
          </div>
          <div className="mt-6 border-l-4 border-red-600 pl-4 py-2 italic font-black text-slate-800 text-lg md:text-xl font-sans leading-relaxed">
            "It’s not just rejection. It’s the frustration of having excellent tech skills but getting blocked by spoken English anxiety."
          </div>
        </div>
      </section>

      {/* 3. Behind the Pain (Storytelling Case Study) */}
      <section className="bg-white py-16 border-t border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4 flex justify-center">
            <MentorAvatar />
          </div>
          <div className="md:col-span-8 space-y-4 font-sans">
            <span className="text-xs font-black uppercase tracking-wider text-[#8B0000]">Small Town Success Story</span>
            <h3 className="text-2xl font-bold font-serif text-slate-900 tracking-tight leading-snug">
              Priya studied in a Hindi-medium school in a small village. She knew her code, but interviews felt impossible.
            </h3>
            <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
              "I knew exactly how databases work. But the moment an interviewer spoke in fast, high-accent English, my throat constricted. My voice cracked. Mr. Vedula did 1-on-1 mock trials with me. He gave me simple verbal frameworks to answer standard technical and behavioral queries. I cracked my first MNC software associate interview!"
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <div className="text-xs font-bold uppercase tracking-wider bg-rose-500/10 text-rose-700 px-3 py-1 rounded-full border border-rose-500/20">
                Software Associate Role
              </div>
              <div className="text-xs font-bold uppercase tracking-wider bg-amber-500/10 text-amber-700 px-3 py-1 rounded-full border border-amber-500/20">
                Overcame Vernacular Barriers
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Coach Feedback Snippets */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6 font-sans">
          <span className="text-xs font-black uppercase tracking-wider text-[#8B0000]">Exclusive Interactive Tool</span>
          <h3 className="text-2xl sm:text-3xl font-bold font-serif tracking-tight">Audio Coaching Snippets</h3>
          <p className="text-slate-500 text-sm max-w-xl mx-auto">
            Click to hear simulated feedback from Mr. Vedula diagnosing real verbal interview pitfalls and providing direct coaching anchors.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="bg-white p-5 rounded-2xl border border-slate-200 flex flex-col justify-between items-start text-left">
              <div>
                <span className="text-xs font-black uppercase text-amber-500 tracking-wider block mb-1">Mistake: English Hesitation Words</span>
                <p className="text-sm font-semibold text-slate-800">"Using too many pauses and fillers due to translations."</p>
              </div>
              <button
                onClick={() => playFeedbackAudio("drifting")}
                className="mt-4 flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#8B0000] text-white rounded-lg hover:bg-[#6B0000] transition-colors"
                data-testid="audio-btn-drifting"
              >
                <Volume2 className="h-4 w-4" /> {playingAudio === "drifting" ? "Playing Audio Comment..." : "Listen to Coaching"}
              </button>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 flex flex-col justify-between items-start text-left">
              <div>
                <span className="text-xs font-black uppercase text-amber-500 tracking-wider block mb-1">Mistake: Introducing Yourself Weakly</span>
                <p className="text-sm font-semibold text-slate-800">"Focusing only on college name instead of your real tech skills."</p>
              </div>
              <button
                onClick={() => playFeedbackAudio("design")}
                className="mt-4 flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#8B0000] text-white rounded-lg hover:bg-[#6B0000] transition-colors"
                data-testid="audio-btn-design"
              >
                <Volume2 className="h-4 w-4" /> {playingAudio === "design" ? "Playing Audio Comment..." : "Listen to Coaching"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Solution Framework */}
      <section className="py-16 md:py-24 bg-[#1A1A1A] text-white font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-black uppercase tracking-wider text-rose-400">The Blueprint</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-serif tracking-tight text-white" data-testid="solution-heading">
              Your Career GPS System
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              Our structured career system coordinates your engineering expertise into direct, high-value alignment.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center font-black">01</div>
              <h3 className="font-extrabold text-lg text-white">Resume Rescue</h3>
              <p className="text-slate-400 text-sm">
                Optimize your resume vectors, bypass robotic filters, and craft strong keyword bullets that recruiters notice.
              </p>
            </div>
            
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center font-black">02</div>
              <h3 className="font-extrabold text-lg text-white">Interview Anchor</h3>
              <p className="text-slate-400 text-sm">
                Reposition behavioral scripts, maintain unshakable design clarity, and command your confidence alignment.
              </p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center font-black">03</div>
              <h3 className="font-extrabold text-lg text-white">Gap Fillers</h3>
              <p className="text-slate-400 text-sm">
                Incorporate robust docker models, scale multi-tenant architectures, and produce real proof of development.
              </p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center font-black">04</div>
              <h3 className="font-extrabold text-lg text-white">Dream 50 Tracker</h3>
              <p className="text-slate-400 text-sm">
                Organically identify senior roles in 50 high-leverage target engineering organizations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Silver Membership Pitch */}
      <section className="py-16 md:py-24 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#8B0000] text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl font-sans">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl" />
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative">
            <div className="md:col-span-8 space-y-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-amber-400 text-slate-950">
                👑 VIP Career Accelerator
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold font-serif tracking-tight" data-testid="silver-heading">
                Upgrade to Silver Membership
              </h2>
              <div className="space-y-3 text-slate-100 text-sm">
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Interview Anchor Toolkit</strong> (Complete STAR behavioral script database)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Resume Rescue Blueprint</strong> (FAANG layout templates)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>Gap Fillers Workbook</strong> (Real production architecture models)
                </p>
                <p className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-amber-400 stroke-[3]" /> <strong>90-Day Production Playbook</strong> (Lifetime roadmap framework)
                </p>
              </div>
              <div className="pt-2">
                <span className="text-xs text-amber-300 font-bold block mb-1">🎁 EXCLUSIVE BONUSES INCLUDED:</span>
                <span className="text-xs text-rose-100 font-medium">3 Free 1-on-1 Clarity Calls + Lifetime Weekly Q&A Webinar Masterminds</span>
              </div>
            </div>
            
            <div className="md:col-span-4 bg-black/25 border border-white/10 p-6 rounded-2xl text-center space-y-4">
              <div className="text-rose-200 text-xs font-black uppercase">Lifetime Package Price</div>
              <div className="text-4xl font-black text-amber-400">₹4,999</div>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a187ad45eaf79ab7261b65e", "Join Silver Membership Now", "Interview Strategy Silver Membership")}
                className="w-full py-4 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-sm uppercase rounded-xl shadow-lg transition-all"
                data-testid="join-silver-membership-btn"
              >
                Join Silver Now
              </button>
              <span className="text-[10px] text-rose-200 block">Immediate Digital Access Granted</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Options Section */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 font-sans">
        <div className="text-center space-y-2">
          <span className="text-xs font-black uppercase tracking-wider text-[#8B0000]">Secure Your Spot</span>
          <h2 className="text-2xl sm:text-3xl font-bold font-serif tracking-tight">Structured Growth Pathways</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-slate-200 bg-white p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-[#8B0000] uppercase tracking-widest block">1-on-1 Coaching</span>
              <h3 className="font-extrabold text-xl text-slate-900">Free Clarity Call</h3>
              <p className="text-slate-500 text-sm">
                Complete a personalized diagnostics call with Mr. Vedula to locate speech blocks and layout errors.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Interview Strategy Free Call")}
              className="w-full mt-6 py-3.5 bg-[#8B0000] hover:bg-[#6B0000] text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-clarity-call-btn"
            >
              Book Call (3 slots left)
            </button>
          </div>

          <div className="border border-slate-200 bg-white p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-[#8B0000] uppercase tracking-widest block">Live Masterclass</span>
              <h3 className="font-extrabold text-xl text-slate-900">Exclusive Live Webinar</h3>
              <p className="text-slate-500 text-sm">
                Acquire STAR behavioural templates live and see real interview diagnostics executed in real time.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Interview Strategy Live Masterclass")}
              className="w-full mt-6 py-3.5 bg-slate-800 hover:bg-slate-900 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-webinar-btn"
            >
              Register for Webinar
            </button>
          </div>

          <div className="border border-amber-300 bg-amber-500/5 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-amber-600 uppercase tracking-widest block">Premium Roadmap</span>
              <h3 className="font-extrabold text-xl text-slate-950">Silver Membership</h3>
              <p className="text-slate-600 text-sm">
                Lifetime masterclass workbook, structured behavioral scripts, and priority alignment feedback access.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://vedulaprogress.lovable.app", "Join Silver Membership Now", "Interview Strategy Silver Membership")}
              className="w-full mt-6 py-3.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-silver-btn"
            >
              Join Silver Membership
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
